import React, { useCallback, useEffect, useRef, useState } from "react";
import Resources from "../../../types/Resources";
import WorkPermitSearchResult from "../../../types/WorkPermitSearchResult";
import { debounce } from "../../../common";
import Result from "../../../types/Result";
import ProblemDetails from "../../../types/responses/ProblemDetails";
import { searchWorkPermitsForConnection } from "../../../apiClient";
import { PermitType } from "../../../common/enums";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";
import DivingChecklistFormState from "../../../types/forms/Permits/Conditions/DivingChecklistFormState";
import { PermitDocument } from "../../../types/permits";

type DivingChecklistModalProps = {
    resources: Resources;
    formState: DivingChecklistFormState;
    canCreateDivingPermits: boolean;
    onChange: (fn: (prevValue: DivingChecklistFormState) => DivingChecklistFormState) => void;
    show: boolean;
    onHide: () => void;
    canManageContent: boolean;
};

function DivingChecklistModal({
    resources,
    formState,
    onChange,
    show,
    onHide,
    canCreateDivingPermits,
    canManageContent
}: DivingChecklistModalProps) {
    const [searchInput, setSearchInput] = useState("");
    const [searchResult, setSearchResult] = useState<WorkPermitSearchResult[]>([]);
    const [searchResultDropdownIsOpen, setSearchResultDropdownIsOpen] = useState(false);

    const searchSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function clickHandler(e: MouseEvent) {
            if (!searchSectionRef.current || !searchSectionRef.current.contains(e.target as Node)) {
                setSearchResultDropdownIsOpen(false);
            }
        }

        document.addEventListener("mousedown", clickHandler);
        return () => document.removeEventListener("mousedown", clickHandler);
    }, []);

    const debounceSearch = useCallback(
        debounce(async (input: string[]) => {
            if (input.length === 0 || !input.some((str) => str && str.length > 0)) {
                setSearchResultDropdownIsOpen(false);
                return;
            }

            const inputString: string = input[0];

            const workPermitSearchResult: Result<WorkPermitSearchResult[], ProblemDetails> =
                await searchWorkPermitsForConnection(inputString, PermitType.DivingPermit);

            if (workPermitSearchResult.failed) {
                console.error(workPermitSearchResult.error);
                setSearchResultDropdownIsOpen(false);
                Swal.fire({
                    icon: "error",
                    text: resources.get("searchError")
                });

                return;
            }

            setSearchResult(workPermitSearchResult.value);
            setSearchResultDropdownIsOpen(true);
        }, 300),
        []
    );

    async function searchInputChangeHandler(newValue: string) {
        setSearchInput(newValue);
        debounceSearch(newValue);
    }

    function sequenceIdentifierChangeHandler(newValue: string) {
        onChange((prevValue) => ({
            ...prevValue,
            divingPermitSequenceIdentifier: {
                ...prevValue.divingPermitSequenceIdentifier,
                value: newValue
            }
        }));
    }

    function searchResultClickHandler(clickedResultSequenceIdentifier: string) {
        sequenceIdentifierChangeHandler(clickedResultSequenceIdentifier);
        setSearchInput("");
        setSearchResultDropdownIsOpen(false);
    }

    const searchResultListItems = searchResult.map((r) => {
        const positionComments = r.positions
            .filter((r) => r.comment)
            .map((p) => p.comment)
            .join(", ");

        return (
            <li
                key={r.sequenceIdentifier}
                onClick={() => searchResultClickHandler(r.sequenceIdentifier)}
                className="list-group-item p-2 border-top"
            >
                {r.sequenceIdentifier}, {r.status}, {positionComments}, {r.companyName}, {r.description}
            </li>
        );
    });

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("ChecklistDiving")}</h1>
            </Modal.Header>
            <Modal.Body>
                {canCreateDivingPermits ? (
                    <p className="fw-bold">
                        <span className="me-1">{resources.get("DivingChecklistInfo0")}</span>
                        <a href="/Permits/DivingPermit" target="_blank" className="me-1">
                            {resources.get("DivingChecklistInfo1")}
                        </a>
                        <span>{resources.get("DivingChecklistInfo2")}</span>{" "}
                    </p>
                ) : (
                    <div className="alert alert-danger fw-bold" style={{ fontSize: "18px" }}>
                        <span className="me-1">
                            {resources.get("DivingChecklist_PortControlCreatePermissionMissing0")}
                        </span>
                        <a href="mailto:hetarbeteneh@portgot.se" target="_blank" className="me-1">
                            hetarbeteneh@portgot.se
                        </a>
                        <span>{resources.get("DivingChecklist_PortControlCreatePermissionMissing1")}</span>
                    </div>
                )}
                <div>
                    <label htmlFor="diving-permit-sequence-identifier">
                        {resources.get("DivingPermitSequenceIdentifier")}
                    </label>
                    <input
                        type="text"
                        id="diving-permit-sequence-identifier"
                        className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                        style={{ width: "256px" }}
                        value={formState.divingPermitSequenceIdentifier.value ?? ""}
                        onChange={(e) => sequenceIdentifierChangeHandler(e.target.value)}
                        readOnly={!canManageContent}
                    />
                </div>
                <div className="mt-3" ref={searchSectionRef}>
                    {canManageContent ? (
                        <>
                            <label htmlFor="diving-permit-search-input">{resources.get("SearchLabel")}</label>
                            <input
                                id="diving-permit-search-input"
                                placeholder={resources.get("SearchPlaceholder")}
                                type="text"
                                className="form-control"
                                value={searchInput ?? ""}
                                onChange={(e) => searchInputChangeHandler(e.target.value)}
                            />
                            <div
                                id="diving-permit-search-results"
                                className="list-group work-permit-search-results"
                                style={{ maxHeight: "300px", overflowY: "auto" }}
                            >
                                {!searchResultDropdownIsOpen ? null : searchResult.length === 0 ? (
                                    <div>{resources.get("NoPermitSearchResults")}</div>
                                ) : (
                                    <ul className="list-unstyled work-permit-search-results border rounded">
                                        {searchResultListItems}
                                    </ul>
                                )}
                            </div>
                        </>
                    ) : null}
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default DivingChecklistModal;
